import { useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0Link } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import DOMPurify from 'dompurify'
import {
  LoadContentDocument,
  MyTrainingsCatalogTrainingFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import 'modules/trainings/components/Revamp/TrainingsDetail/Overview/MainContent/index.styles.css'
import { OverviewTag } from 'modules/trainings/components/Revamp/TrainingsDetail/Overview/OverviewTag'
import Syllabus from 'modules/trainings/components/content/Syllabus'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

export const Overview = () => {
  const { training, hasContentFF } = useOutletContext<{
    training: MyTrainingsCatalogTrainingFragment
    hasContentFF: boolean
  }>()

  const instructors = training?.sessionsConnection?.nodes?.length
    ? training?.sessionsConnection?.nodes?.flatMap((session) =>
        session.sessionInstructors.map((instructor) => ({
          firstName: instructor.employee.firstName,
          lastName: instructor.employee.lastName,
          type: 'person' as const,
          src: buildUrl(instructor.employee?.avatar?.url ?? ''),
        }))
      )
    : []
  const sanitizedLink = DOMPurify.sanitize(training?.learningPlatform?.link ?? '')

  const { data: contentData, loading: contentLoading } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(training.id),
    },
  })

  const collection = useMemo(() => {
    const modules = contentData?.trainings.contentModules
    if (contentLoading || !modules) {
      return null
    }
    return graphqlContentMapper.fromGraphQL(modules, Number(training.id))
  }, [training.id, contentData, contentLoading])

  // this is not the best solution, but we need to show the syllabus only if there is content
  const showContent = Number(collection?.modules.length) > 0

  return (
    <Box flexDirection="row" gap="s24">
      <Box style={{ flex: '2' }} gap="s24">
        <Box gap="s8">
          <Text.Normal size="300" weight="semibold">
            {i18n.t('trainings.my_trainings_revamp.training_detail.overview.competencies')}
          </Text.Normal>
          <Box gap="s8" flexDirection="row" flexWrap="wrap">
            {training.competencies?.length
              ? training.competencies.map((competency) => (
                  <OverviewTag key={competency.id} tagName={competency.name} />
                ))
              : '-'}
          </Box>
        </Box>
        <Box gap="s8">
          <Text.Normal weight="semibold">
            {i18n.t('trainings.my_trainings_revamp.training_detail.overview.objectives')}
          </Text.Normal>
          <Box className={'MainContent_textDisplay'}>
            <RichTextDisplay content={training?.objectives || '-'} />
          </Box>
        </Box>
        <Box gap="s8">
          <Text.Normal weight="semibold">
            {i18n.t('trainings.my_trainings_revamp.training_detail.overview.description')}
          </Text.Normal>
          <Box className={'MainContent_textDisplay'}>
            <RichTextDisplay content={training?.description || '-'} />
          </Box>
        </Box>
        {instructors?.length > 0 && (
          <Box gap="s8">
            <Text.Normal size="300" weight="semibold" color="textPrimary">
              {i18n.t('trainings.my_trainings_revamp.training_detail.overview.instructors')}
            </Text.Normal>

            <F0AvatarList avatars={instructors} size="sm" type="person" max={3} />
          </Box>
        )}
        {training?.learningPlatform?.link && (
          <Box gap="s8">
            <Text.Normal size="300" weight="semibold" className="text-f1-foreground">
              {i18n.t('trainings.my_trainings_revamp.training_detail.overview.learning_platform')}
            </Text.Normal>
            <Box alignItems="flexStart">
              <F0Link href={sanitizedLink}>{sanitizedLink}</F0Link>
            </Box>
          </Box>
        )}
        {hasContentFF && showContent && (
          <Box gap="s8">
            <Text.Normal size="300" weight="semibold">
              {i18n.t('trainings.trainings_revamp.training_detail.overview.course_content')}
            </Text.Normal>
            <Box paddingLeft="s20" paddingRight="s20">
              <Syllabus trainingId={training.id} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
